
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useAddItemMutation } from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import type { RouteInfo } from "@/modules/mine/navigation/types";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { InputGroup, InputGroupInput } from "@/shared/ui/input-group";
import { Label } from "@/shared/ui/label";

const addLinkFormSchema = z.object({
  url: z.string().url({
    message: "请输入合格的链接",
  }),
  title: z.string().optional(),
});

type AddLinkResult = {
  action: "created";
  folderId: string | null;
};

interface AddLinkProps extends DialogComponentProps<AddLinkResult> {
  routeInfo: RouteInfo;
}

export function AddLink({ close, resolve, routeInfo }: AddLinkProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(routeInfo.folderId || null);
  const addItemMutation = useAddItemMutation();

  const form = useForm<z.infer<typeof addLinkFormSchema>>({
    resolver: zodResolver(addLinkFormSchema),
    defaultValues: {
      url: "",
      title: "",
    },
  });

  function onSubmit(values: z.infer<typeof addLinkFormSchema>) {
    addItemMutation.mutate(
      {
        url: values.url,
        title: values.title?.trim() || undefined,
        folder_id: selectedFolderId ?? undefined,
        format: "website",
      },
      {
        onSuccess: () => {
          form.reset();
          toast.success("添加成功");
          resolve({
            action: "created",
            folderId: selectedFolderId,
          });
        },
      },
    );
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel className="text-xs text-muted-foreground">链接 URL</FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput {...field} id="url" placeholder="请输入链接" />
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel className="text-xs text-muted-foreground">标题</FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput {...field} id="title" placeholder="请输入标题" />
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

         
          {/* 文件夹选择 */}
          <FormItem className="mb-4">
            <Label className="text-xs text-muted-foreground">文件夹</Label>
            <FolderSelector
              value={selectedFolderId}
              onChange={setSelectedFolderId}
              placeholder="未分类"
              allowEmpty={true}
              emptyLabel="未分类"
            />
          </FormItem>
        </form>
      </Form>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={close}
          disabled={addItemMutation.isPending}
        >
          取消
        </Button>
        <Button
          type="button"
          onClick={form.handleSubmit(onSubmit)}
          disabled={addItemMutation.isPending}
        >
          确认
          {addItemMutation.isPending && <Loader2 className="animate-spin" />}
        </Button>
      </DialogFooter>
    </>
  );
}
